import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'umi';
import { usePrevious, useSafeState, useUpdate } from 'ahooks';
import _ from 'lodash-es';
import { Button, Bubble, notification, Modal, Input } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import AceEditor from 'react-ace';
import { namespace } from '@/pages/aigc/pages/Text2SQL/model';
import { copyTextToClipboard } from '@/pages/datalab/utils/utils';
import { checkSql, updateSqlLike } from '@/pages/aigc/pages/Text2SQL/service';
import { objectToFormData } from '@/pages/aigc/utils/utils';
import 'ace-builds/src-noconflict/mode-mysql';
import 'ace-builds/src-noconflict/theme-monokai';

export default function ShowSql(props) {
  const {
    chatError,
    relevantTables,
    sqlInfo,
    dispatch,
    chatLoading,
    gameCode,
    text2sqlFormat,
    refCloneDeep,
    isEn,
  } = props;
  const [visible, setVisible] = useState(false);
  const [readonlyCode, setReadonlyCode] = useState(sqlInfo?.code);
  const [checkResult, setCheckResult] = useState(null);
  const [likeState, setLikeState] = useState('');

  // 这个值来存储上一次的状态，修复按钮  有两种情况，成功，失败
  // 如果失败，需要回滚上一次状态，失败后端是没有返回sql这个字段的，需要前端自己补上
  // 如果成功，并且也有sql，但是返回的sql也有问题，那就会检测到语法错误，有点死循环，如果返回的sql没问题，那就正常用后端展示的
  const sqlInfoCloneDeep = usePrevious(readonlyCode);

  useEffect(() => {
    setLikeState('');
    setReadonlyCode(sqlInfo?.code);

    checkSql(
      objectToFormData({ sql: sqlInfo?.code, id: sqlInfo?.id, game: gameCode }),
    ).then(({ text_content, status }) => {
      setCheckResult({ content: text_content, status });
    });
  }, [gameCode, sqlInfo]);
  // console.log(sqlInfo)
  // console.log(sqlInfoCloneDeep)
  // console.log(refCloneDeep)
  // console.log(relevantTables && !sqlInfo && (!!(!sqlInfoCloneDeep || _.isEmpty(refCloneDeep))))
  // console.log(relevantTables && !sqlInfo && (!!(!sqlInfoCloneDeep && _.isEmpty(refCloneDeep))))

  // 已经有表数据, 但是还没有SQL,B并且重试数据还存储
  if (
    relevantTables &&
    !sqlInfo &&
    !!(!sqlInfoCloneDeep && _.isEmpty(refCloneDeep))
  ) {
    // 如果此时聊天出错
    if (chatError) {
      return <div className={'center'}>{text2sqlFormat('sql_error')}</div>;
    }
    return (
      <Button
        className={'center'}
        type="primary"
        disabled={chatLoading}
        onClick={() => {
          dispatch({
            type: `${namespace}/rebuildTableOrSql`,
            payload: {
              message: text2sqlFormat('generate_sql'),
              isEn,
            },
          });
        }}
      >
        {text2sqlFormat('generate_sql')}
      </Button>
    );
  }

  // 已经有表数据, 并且也有SQL了, 但是聊天还是返回了错误
  if (relevantTables && sqlInfo && chatError) {
    return (
      <div className={'center'}>
        <p>{chatError.message}</p>
        <Button
          type="primary"
          disabled={chatLoading}
          onClick={() => {
            dispatch({
              type: `${namespace}/rebuildTableOrSql`,
              payload: {
                message: text2sqlFormat('sql_rebuild'),
                isEn,
              },
            });
          }}
        >
          {text2sqlFormat('sql_rebuild')}
        </Button>
      </div>
    );
  }
  return (
    <>
      <ul className={'sql-info'}>
        {sqlInfo?.desc ||
          refCloneDeep?.desc
            ?.split('\n')
            ?.map((info: string, index: number) =>
              info ? <li key={index}>{info}</li> : '',
            )}
      </ul>
      <SqlEditor
        code={readonlyCode || sqlInfoCloneDeep}
        readonly={true}
        onEditor={() => setVisible(true)}
        text2sqlFormat={text2sqlFormat}
      />
      <div className={'sql-card-footer'}>
        {checkResult ? <CheckSqlResult result={checkResult} {...props} /> : ''}
        <FeedBack
          id={sqlInfo?.id}
          likeState={likeState}
          setLikeState={setLikeState}
        />
      </div>

      <SqlModal
        text2sqlFormat={text2sqlFormat}
        visible={visible}
        sqlInfo={sqlInfo}
        onClose={() => setVisible(false)}
        checkSql={(code: string) =>
          checkSql(
            objectToFormData({
              sql: code || sqlInfoCloneDeep,
              id: sqlInfo?.id || refCloneDeep.id || -1,
              game: gameCode,
            }),
          )
        }
        onSave={(code: string, result) => {
          setReadonlyCode(code);
          setCheckResult(result);
        }}
      />
    </>
  );
}

// SQL代码展示和编辑
function SqlEditor(props) {
  const { code, readonly = false, onEditor, onChange, text2sqlFormat } = props;
  const [copyIcon, setCopyIcon] = useState('datalab-copy');
  return (
    <div className={'sql-editor'}>
      <AceEditor
        height="312px"
        aria-label="query editor input"
        mode="mysql"
        theme="monokai"
        fontSize={14}
        width="100%"
        showPrintMargin={false}
        showGutter={true}
        value={code}
        readOnly={readonly}
        onChange={onChange}
      />
      {onEditor ? (
        <Bubble dark trigger="hover" content={text2sqlFormat?.('sql_edit')}>
          <div className={'sql-editor-btn-icon edit'} onClick={onEditor}>
            <SvgIcon type={'pencil-m'} />
          </div>
        </Bubble>
      ) : (
        ''
      )}

      <Bubble
        dark
        trigger="hover"
        content={
          copyIcon === 'datalab-copy'
            ? text2sqlFormat('sql_copy')
            : text2sqlFormat('sql_copied')
        }
      >
        <div
          className={`sql-editor-btn-icon ${copyIcon}`}
          onClick={() => {
            copyTextToClipboard(code)?.then(
              () => {
                setCopyIcon('right-good');
              },
              (err) => {
                console.error('SQL复制失败: ', err);
              },
            );
          }}
          onMouseLeave={() => {
            setCopyIcon('datalab-copy');
          }}
        >
          <SvgIcon type={copyIcon} />
        </div>
      </Bubble>
    </div>
  );
}

// 编辑SQL弹窗
function SqlModal(props) {
  const { sqlInfo, onSave, visible, onClose, checkSql, text2sqlFormat } = props;
  const [editCode, setEditCode] = useState(sqlInfo?.code);
  const [checkResult, setCheckResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const sqlInfoCloneDeep = usePrevious(editCode);
  const cancleHandle = () => {
    onClose();
    setEditCode(sqlInfo?.code);
  };
  useEffect(() => {
    setEditCode(sqlInfo?.code);
    setCheckResult(null);
  }, [sqlInfo]);

  return (
    <Modal
      visible={visible}
      onClose={cancleHandle}
      size={'xl'}
      caption={'SQL'}
      className={'sql-modal'}
    >
      <Modal.Body>
        <SqlEditor
          text2sqlFormat={text2sqlFormat}
          code={editCode || sqlInfoCloneDeep}
          onChange={(value: string) => {
            setEditCode(value);
          }}
        />
        {checkResult ? <CheckSqlResult result={checkResult} /> : ''}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={loading}
          onClick={() => {
            setLoading(true);
            checkSql(editCode || sqlInfoCloneDeep)
              .then((result) => {
                const { text_content: content, status } = result;
                onSave(editCode || sqlInfoCloneDeep, { content, status });
                setCheckResult({ content, status });
              })
              .catch((e) => {
                console.error('校验SQL失败', e);
                onSave(editCode || sqlInfoCloneDeep, null);
                setCheckResult(null);
              })
              .finally(() => {
                notification.success({
                  description: text2sqlFormat('sql_save_success'),
                });
                onClose();
                setLoading(false);
              });
          }}
        >
          {text2sqlFormat('sql_save')}
        </Button>
        <Button
          onClick={() => {
            cancleHandle();
          }}
        >
          {text2sqlFormat('sql_cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// SQL校验结果
function CheckSqlResult(props) {
  const {
    result,
    text2sqlFormat,
    dispatch,
    isEn,
    messages,
    chatLoading,
    relevantTables,
    ...restProps
  } = props;
  const [offOneClick, setoffOneClick] = useSafeState<
  'success' | 'error' | unknown
  >('');
  const offOne = useRef<boolean>(false);
  const sqlInfoCloneDeep = usePrevious(messages);
  // 错误回调
  const handleRetryFetchSql = _.throttle(
    useCallback(() => {
      if (result?.status === 0) return;
      offOne.current = true;
      window.sessionStorage.setItem(
        'isGrammaticalMistake',
        JSON.stringify(true),
      );
      dispatch({
        type: `${namespace}/rebuildTableOrSql`,
        payload: {
          message: text2sqlFormat('syntax_retry_fixes'),
          isSyntaxIncorrect: `${text2sqlFormat('syntax_retry_fixes')}:${result?.content
          }`,
          isEn,
        },
      });
    }, [dispatch, isEn, result?.content, result?.status, text2sqlFormat]),
    2000,
  );
  useEffect(() => {
    // 语法请求效验成功
    if (result?.status === 0) {
      // 重试按钮标识点击
      if (offOne.current) {
        //  重试按钮点击要不成功或者失败，这里假设成功，不成功，直接会走到失败处渲染视图，反之，正常，并将本次点击的按钮标识重置
        setoffOneClick('success');
        offOne.current = false;
        return;
      }
      // 当我们的聊天框变化，每次都是自增，这里需要把上一次重试按钮的标识清空，如果直接点击了清空内容，整个数据会重置，
      // 但是有一种情况，在请求wait中，调了查询sql效验语法的接口，也应该取消，不然会有诡异的问题，有时间需要处理一下
      // 当长度一样，返回数据时，这里在重置
      if (sqlInfoCloneDeep?.length + 1 === messages?.length && !chatLoading) {
        setoffOneClick('');
        return;
      }
      // 请求失败
    } else {
      setoffOneClick('error');
    }
    // 当状态下次变化时。首先直接把点击标识从全局取消
    return () => {
      window.sessionStorage.setItem(
        'isGrammaticalMistake',
        JSON.stringify(false),
      );
    };
  }, [chatLoading, messages, result?.status, setoffOneClick, sqlInfoCloneDeep?.length]);
  // 渲染各个结果的message
  const renderComponents = useMemo(() => {
    const initialStatMessage =
      result?.status === 0 ? 'syntax_completed' : 'syntax_fixes';
    const isCancel = window.sessionStorage.getItem('isCancel');
    if (result?.status !== 0) {
      return (
        <>
          {result?.status !== 0 && (
            <Button
              type="error"
              className="error-btn"
              onClick={handleRetryFetchSql}
            >
              {text2sqlFormat?.(initialStatMessage)}
            </Button>
          )}
          {result?.content}
        </>
      );
    }
    if (offOneClick === 'success') {
      if (!offOne.current) {
        return text2sqlFormat?.(initialStatMessage);
      }
      return text2sqlFormat?.('syntax_check_passed');
    }
    if (isCancel && JSON.parse(isCancel)) {
      window.sessionStorage.setItem('isCancel', JSON.stringify(false));
      // return text2sqlFormat?.(initialStatMessage);
      if (offOneClick === 'success') {
        return text2sqlFormat?.(initialStatMessage);
      }
      return text2sqlFormat?.('syntax_check_passed');
    }
    return text2sqlFormat?.('syntax_check_passed');
  }, [
    handleRetryFetchSql,
    offOneClick,
    result?.content,
    result?.status,
    text2sqlFormat,
  ]);

  return (
    <div
      className={`check-sql-result ${result?.status === 0 ? 'success' : 'error'
      }`}
    >
      {renderComponents}
    </div>
  );
}

// 点赞点踩

function FeedBack({ id, likeState, setLikeState }) {
  const { formatMessage } = useIntl();
  const [visible, setVisible] = useState(false);
  const [comment, setComment] = useState('');

  const close = () => setVisible(false);
  return (
    <div className={'sql-feedback'}>
      <Bubble
        dark
        arrowPointAtCenter
        content={formatMessage({
          id: 'dbh_feedback_isgod_tips',
        })}
        trigger="click"
        closeOnScroll
      >
        <i
          onClick={() => {
            setLikeState(0);
            updateSqlLike(objectToFormData({ id, score: 1 }));
          }}
          className={likeState === 0 ? 'checked' : ''}
        >
          <SvgIcon
            className="feedback-icon"
            type={likeState === 'like' ? 'like-all' : 'very-good'}
          />
        </i>
      </Bubble>

      <i
        onClick={() => {
          setVisible(true);
        }}
        className={likeState < 0 ? 'checked' : ''}
      >
        <SvgIcon className="feedback-icon" type={'not-work'} />
      </i>

      <Modal
        visible={visible}
        caption={formatMessage({ id: 'aigc_text2sql_feedback_title' })}
        onClose={close}
        className={'sql-feedback-modal'}
      >
        <Modal.Body>
          <Input.TextArea
            showCount
            // size={size}
            value={comment}
            onChange={(value) => {
              setComment(value);
            }}
            placeholder={formatMessage({
              id: 'aigc_text2sql_feedback_placeholder',
            })}
            maxLength={500}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            onClick={() => {
              setLikeState(-1);
              updateSqlLike(objectToFormData({ id, score: -1, comment }));
              setComment('');
              close();
            }}
          >
            {formatMessage({ id: 'aigc_common_confirm' })}
          </Button>
          <Button type="weak" onClick={close}>
            {formatMessage({ id: 'aigc_common_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
